import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CheckCircle, Clock, Headphones, Settings, TrendingUp, ArrowRight, Phone, Users } from 'lucide-react';

export default function ServiciosGestionados() {
  const caracteristicas = [
    {
      icon: Clock,
      titulo: 'Soporte 24/7/365',
      descripcion: 'Monitoreo continuo y soporte técnico disponible todos los días del año.'
    },
    {
      icon: Headphones,
      titulo: 'Service Desk Dedicado',
      descripcion: 'Equipo especializado que conoce tu infraestructura y responde en minutos.'
    },
    {
      icon: Settings,
      titulo: 'Mantenimiento Proactivo',
      descripcion: 'Actualizaciones, parches y optimización preventiva de tu infraestructura.'
    },
    {
      icon: TrendingUp,
      titulo: 'SLAs Garantizados',
      descripcion: 'Acuerdos de nivel de servicio con penalidades por incumplimiento.'
    }
  ];

  const servicios = [
    {
      categoria: 'Infrastructure Management',
      descripcion: 'Gestión completa de tu infraestructura on-premise, cloud e híbrida',
      incluye: [
        'Monitoreo 24/7 de servidores, storage, redes',
        'Gestión de incidentes con SLA de respuesta',
        'Aplicación de parches y actualizaciones',
        'Capacity planning y reportes mensuales',
        'Change management controlado',
        'Backup verification y disaster recovery testing'
      ],
      sla: 'Respuesta: 15 min crítico, 1 hora alto, 4 horas medio'
    },
    {
      categoria: 'Cloud Operations (CloudOps)',
      descripcion: 'Operación y optimización de ambientes multi-cloud',
      incluye: [
        'Gestión de recursos AWS, Azure, GCP',
        'Cost optimization y showback/chargeback',
        'Seguridad y compliance continuo',
        'Automatización de tareas repetitivas',
        'Performance tuning y escalamiento',
        'Reportes ejecutivos de uso y costos'
      ],
      sla: 'Disponibilidad: 99.9% | Respuesta: < 30 min'
    },
    {
      categoria: 'Security Operations (SecOps)',
      descripcion: 'Security Operations Center (SOC) con analistas especializados',
      incluye: [
        'Monitoreo SIEM 24/7 con analistas',
        'Threat hunting proactivo',
        'Incident response y forensics',
        'Vulnerability management',
        'Compliance reporting (ISO, PCI, GDPR)',
        'Security awareness training'
      ],
      sla: 'Detección: < 5 min | Contención: < 1 hora'
    },
    {
      categoria: 'Database Administration (DBA)',
      descripcion: 'Administración experta de bases de datos empresariales',
      incluye: [
        'Monitoreo de performance y queries',
        'Backup y recovery automatizado',
        'Optimización de índices y queries',
        'Aplicación de parches de seguridad',
        'High availability y failover',
        'Capacity planning de storage'
      ],
      sla: 'Disponibilidad: 99.95% | Recovery Time: < 4 horas'
    },
    {
      categoria: 'Application Support',
      descripcion: 'Soporte L1/L2/L3 para aplicaciones críticas del negocio',
      incluye: [
        'Service desk multicanal (portal, email, phone)',
        'Troubleshooting de aplicaciones',
        'Coordinación con vendors (SAP, Oracle, Microsoft)',
        'Gestión de cambios en aplicaciones',
        'Performance monitoring de apps',
        'User onboarding/offboarding'
      ],
      sla: 'Respuesta L1: < 10 min | Resolución: según criticidad'
    },
    {
      categoria: 'Backup & Disaster Recovery',
      descripcion: 'Protección continua de datos y planes de recuperación validados',
      incluye: [
        'Backup automático diario/semanal/mensual',
        'Replicación a sitio secundario',
        'Testing trimestral de DR',
        'Retención configurable (7/30/90 días)',
        'Backup monitoring y alertas',
        'Recovery runbooks documentados'
      ],
      sla: 'RPO: < 4 horas | RTO: < 8 horas'
    }
  ];

  const beneficios = [
    'Reducción de 60% en costos operativos vs equipo interno',
    'Acceso a especialistas certificados (HPE, AWS, VMware, Cisco)',
    'Respuesta garantizada en minutos, no horas',
    'Liberación de tu equipo para proyectos estratégicos',
    'Escalamiento flexible según necesidades del negocio',
    'Tecnología de monitoreo enterprise incluida',
    'Reportes ejecutivos mensuales con KPIs'
  ];

  const modelos = [
    {
      titulo: 'Full Outsourcing',
      descripcion: 'ALLTI opera toda tu infraestructura TI. Ideal para empresas sin equipo técnico interno o que buscan enfocarse 100% en su negocio.',
      icon: RefreshCw
    },
    {
      titulo: 'Co-Managed IT',
      descripcion: 'Tu equipo interno y ALLTI trabajan juntos. Nosotros cubrimos 24/7, soporte especializado y proyectos, tu equipo mantiene control estratégico.',
      icon: Users
    },
    {
      titulo: 'Project-Based',
      descripcion: 'Servicios gestionados por tiempo definido (ej: migración cloud, implementación de solución). Incluye soporte post-implementación.',
      icon: CheckCircle
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/90 via-red-900/85 to-orange-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-orange-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-orange-300 font-semibold">Managed Services 24/7/365</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Servicios Gestionados de TI
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Operación, soporte y optimización continua de tu infraestructura tecnológica
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Cotizar Servicios Gestionados <ArrowRight className="ml-2" />
              </Link>
              <a 
                href="#servicios" 
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Servicios
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Enfócate en tu negocio, nosotros operamos tu TI
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Los <strong>servicios gestionados</strong> te permiten tener un equipo experto operando 
                tu infraestructura 24/7/365 sin necesidad de contratar, capacitar y retener talento técnico 
                especializado. ALLTI se convierte en tu departamento de TI o en extensión de tu equipo actual.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Nuestros <strong>Managed Services</strong> incluyen: monitoreo continuo, soporte técnico 
                multicanal, mantenimiento preventivo, aplicación de parches, backup/recovery, seguridad, 
                y reportes ejecutivos. Todo con SLAs garantizados y modelo de pago mensual predecible.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <RefreshCw className="inline mr-2 text-orange-600" size={24} />
                  ¿Por qué servicios gestionados?
                </p>
                <p className="text-gray-600">
                  Un ingeniero senior cuesta $80K+ USD/año (sin beneficios). Un equipo completo 
                  (NOC, soporte, seguridad, DBAs) puede costar $500K+ anuales. Managed Services 
                  da acceso a todo ese expertise por una fracción del costo.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070" 
                alt="Managed Services"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Qué Incluyen Nuestros Servicios?
            </h2>
            <p className="text-xl text-gray-600">
              Soporte integral para tu tranquilidad
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="bg-orange-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-orange-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.titulo}</h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Portafolio de Servicios Gestionados
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Desde infraestructura hasta aplicaciones y seguridad
            </p>
          </div>
          <div className="space-y-6">
            {servicios.map((servicio, index) => (
              <div 
                key={index}
                className="bg-gradient-to-r from-orange-50 to-white border border-orange-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-orange-100 p-3 rounded-lg">
                        <RefreshCw className="text-orange-600" size={24} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{servicio.categoria}</h3>
                    </div>
                    <p className="text-gray-600 mb-4 text-lg">{servicio.descripcion}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      {servicio.incluye.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="text-hpe-green flex-shrink-0 mt-0.5" size={16} />
                          <span className="text-gray-600 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="lg:w-64 bg-white rounded-lg p-6 border border-orange-300 lg:flex-shrink-0">
                    <p className="text-xs font-semibold text-orange-600 mb-2">SLA GARANTIZADO</p>
                    <p className="text-gray-800 font-medium text-sm">{servicio.sla}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modelos de Servicio */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Modelos de Colaboración
            </h2>
            <p className="text-xl text-gray-600">
              Elige el nivel de soporte que necesitas
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {modelos.map((modelo, index) => {
              const Icon = modelo.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                  <div className="bg-orange-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-orange-600" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{modelo.titulo}</h3>
                  <p className="text-gray-600">{modelo.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Ventajas de Servicios Gestionados
              </h2>
              <p className="text-xl text-orange-100 mb-8">
                Empresas que contratan servicios gestionados con ALLTI experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-hpe-green flex-shrink-0 mt-1" size={24} />
                    <span className="text-lg text-orange-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2084" 
                alt="Team Collaboration"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RefreshCw className="text-orange-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Libera a tu equipo, aumenta tu productividad
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            ALLTI opera infraestructura de cientos de empresas en México. Contamos con ingenieros 
            certificados, NOC 24/7, y herramientas enterprise para garantizar la continuidad de tu negocio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contacto" 
              className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Solicitar Cotización
            </Link>
            <Link 
              to="/casos-de-uso" 
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Clientes Satisfechos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
